import { Card } from "../../app/components/primitives/card";
import {
  CheckCircle2,
  CreditCard,
  FileText,
  Hash,
  ListChecks,
  PencilLine,
  Upload,
  XCircle,
} from "lucide-react";

// The Needs you list, the same on the quote and the order page (Ben, 5 Oct):
// one row per thing the customer owes, each with what and why, its action
// on the right, and room under it for a small inline form (the PO number,
// how you'll pay).

export type NeedsYouIcon =
  "pay" | "po" | "upload" | "cant_print" | "name" | "proof" | "choice";

const ICONS = {
  pay: CreditCard,
  po: Hash,
  upload: Upload,
  cant_print: XCircle,
  name: PencilLine,
  proof: FileText,
  choice: ListChecks,
};

export interface NeedsYouItem {
  key: string;
  icon: NeedsYouIcon;
  title: string;
  why: string;
  action?: React.ReactNode;
  /** Shown under the row: an inline form or a note. */
  below?: React.ReactNode;
  /** Done this visit (a quote's choice made): the row stays, ticked, so a
   *  field being typed into doesn't vanish; it no longer counts. */
  isDone?: boolean;
  /** Shown for information, not counted (a quote line file we can't print:
   *  files are optional before acceptance). */
  isInfo?: boolean;
}

export function NeedsYouList({
  rows,
  emptySentence,
}: {
  rows: NeedsYouItem[];
  emptySentence: string;
}) {
  const isAllDone = rows.every((r) => r.isDone || r.isInfo);
  return (
    <div className="space-y-3">
      {isAllDone && (
        <Card
          role="status"
          tabIndex={-1}
          data-needs-all-done
          className="flex-row items-center gap-3 p-5 focus:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
        >
          <CheckCircle2 className="size-5 shrink-0 text-green-700 dark:text-green-400" />
          <p className="text-sm font-medium">{emptySentence}</p>
        </Card>
      )}
      {rows.length > 0 && <NeedsYouRows rows={rows} />}
    </div>
  );
}

function NeedsYouRows({ rows }: { rows: NeedsYouItem[] }) {
  return (
    <Card className="gap-0 overflow-hidden p-0">
      <ul className="divide-y">
        {rows.map((row) => {
          const Icon = row.isDone ? CheckCircle2 : ICONS[row.icon];
          return (
            <li
              key={row.key}
              tabIndex={-1}
              data-needs-row={row.isDone || row.isInfo ? undefined : "open"}
              className="space-y-2 px-4 py-3 focus:outline-none"
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex min-w-0 items-start gap-2.5">
                  <Icon
                    className={
                      row.isDone
                        ? "mt-0.5 size-4 shrink-0 text-green-700 dark:text-green-400"
                        : row.icon === "cant_print"
                          ? "mt-0.5 size-4 shrink-0 text-red-600 dark:text-red-400"
                          : "mt-0.5 size-4 shrink-0 text-muted-foreground"
                    }
                  />
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{row.title}</p>
                    <p className="text-sm text-muted-foreground">{row.why}</p>
                  </div>
                </div>
                {row.action}
              </div>
              {row.below && <div className="pl-6">{row.below}</div>}
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
